import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Trees,
  Camera,
  Wallet,
  Filter,
  Upload,
  Users,
  ScrollText,
  Settings,
  LogOut,
  TreePine,
  BookOpen,
  Tag,
  Map,
  Download,
  Film,
  MessageSquare,
  Megaphone,
  KeyRound,
  X,
  CloudDownload,
} from 'lucide-react';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { api, apiError } from '@/lib/api-client';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { APP_VERSION } from '@/lib/changelog';

export function Sidebar({
  mobileOpen = false,
  onMobileClose,
}: {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (mobileOpen && onMobileClose) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onMobileClose) onMobileClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mobileOpen, onMobileClose]);
  const clear = useAuthStore((s) => s.clear);
  const pushToast = useUiStore((s) => s.pushToast);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const canManageUsers = isSuperAdmin || user?.role === 'ADMINISTRATOR';

  // Download links freshness — share the cache with DownloadAppPage by
  // re-using the same query key. The badge appears next to the
  // "Download App" sidebar link when there's any per-slot timestamp
  // newer than what the user has seen (tracked in localStorage,
  // stamped when DownloadAppPage mounts).
  const downloadLinksQ = useQuery<{
    timestamps?: Record<string, string>;
  }>({
    queryKey: ['settings', 'download'],
    queryFn: () => api.get('settings/download').json(),
    enabled: !!user, // skip on the login screen
  });
  // Re-read seen-at from storage on every render so the badge clears
  // immediately when DownloadAppPage stamps the value.
  const [seenAtTick, setSeenAtTick] = useState(0);
  useEffect(() => {
    // Bump the tick when the user navigates back to the sidebar so
    // the badge state refreshes after a download-page visit.
    setSeenAtTick((n) => n + 1);
  }, [location.pathname]);
  const latestUpdate = (() => {
    const ts = downloadLinksQ.data?.timestamps ?? {};
    let max = '';
    for (const v of Object.values(ts)) {
      if (typeof v === 'string' && v > max) max = v;
    }
    return max || null;
  })();
  const downloadHasUpdate = (() => {
    if (!latestUpdate) return false;
    void seenAtTick; // dependency: re-read storage on tick
    let seen = '';
    try {
      seen = localStorage.getItem('forest.downloadLinksSeenAt') ?? '';
    } catch {
      // localStorage unavailable (e.g. private mode) — surface the
      // badge once per session in that case rather than missing it.
    }
    return latestUpdate > seen;
  })();

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
      isActive
        ? 'border-l-[3px] border-l-forest-600 bg-forest-50/80 pl-[9px] text-forest-800 dark:bg-forest-900/30 dark:text-forest-200'
        : 'border-l-[3px] border-l-transparent pl-[9px] text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
    );

  const iconClass = (isActive: boolean) =>
    cn(
      'h-[18px] w-[18px] transition-colors',
      isActive
        ? 'text-forest-600 dark:text-forest-300'
        : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300',
    );

  const onLogout = async () => {
    try {
      await api.post('auth/sign-out');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    } finally {
      clear();
      navigate('/login', { replace: true });
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={onMobileClose}
          aria-hidden
        />
      )}
    <nav
      className={cn(
        'flex w-64 flex-col border-r border-slate-200 bg-gradient-to-b from-white via-white to-forest-50',
        'dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900',
        // Mobile: off-canvas drawer
        'fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-in-out lg:static lg:w-56 lg:translate-x-0',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      )}
      aria-hidden={!mobileOpen && typeof window !== 'undefined' && window.innerWidth < 1024}
    >
      {/* Logo / brand mark */}
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3.5 dark:border-slate-800">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-forest-600 text-white shadow-sm">
          <TreePine className="h-4.5 w-4.5" />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold tracking-tight text-forest-800 dark:text-forest-200">Pnerp</span>
            <Link to="/app/beta" className="rounded bg-amber-100 px-1 py-px text-[9px] font-bold uppercase leading-tight text-amber-700 transition-colors hover:bg-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:hover:bg-amber-500/30">Beta</Link>
          </div>
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">Registry</span>
        </div>
        <button
          type="button"
          onClick={onMobileClose}
          className="ml-auto rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 lg:hidden dark:hover:bg-slate-800 dark:hover:text-slate-300"
          aria-label={t('nav.closeMenu')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Main navigation */}
      <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-3">
        <NavLink to="/app/parcels" className={linkClass}>
          {({ isActive }) => (
            <>
              <Trees className={iconClass(isActive)} /> {t('nav.parcels')}
            </>
          )}
        </NavLink>
        <NavLink to="/app/database" className={linkClass}>
          {({ isActive }) => (
            <>
              <Camera className={iconClass(isActive)} /> {t('nav.photos')}
            </>
          )}
        </NavLink>
        {isSuperAdmin ? (
          <NavLink to="/app/works" className={linkClass}>
            {({ isActive }) => (
              <>
                <Wallet className={iconClass(isActive)} /> {t('nav.works')}
              </>
            )}
          </NavLink>
        ) : null}
        <NavLink to="/app/import" className={linkClass}>
          {({ isActive }) => (
            <>
              <Upload className={iconClass(isActive)} /> {t('nav.import')}
            </>
          )}
        </NavLink>
        <NavLink to="/app/filter" className={linkClass}>
          {({ isActive }) => (
            <>
              <Filter className={iconClass(isActive)} /> {t('nav.filter')}
            </>
          )}
        </NavLink>

        {canManageUsers ? (
          <>
            <div className="mb-1 mt-4 px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              {t('nav.admin')}
            </div>
            <NavLink to="/app/admin/users" className={linkClass}>
              {({ isActive }) => (
                <>
                  <Users className={iconClass(isActive)} /> {t('nav.admin.users')}
                </>
              )}
            </NavLink>
            {isSuperAdmin ? (
              <>
                <NavLink to="/app/admin/audit" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <ScrollText className={iconClass(isActive)} /> {t('nav.admin.audit')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/settings" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <Settings className={iconClass(isActive)} /> {t('nav.admin.settings')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/regions" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <Map className={iconClass(isActive)} /> {t('nav.admin.regions')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/download" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <Download className={iconClass(isActive)} /> {t('nav.admin.download')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/app-credentials" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <KeyRound className={iconClass(isActive)} /> {t('nav.admin.appCredentials')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/works-sync" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <CloudDownload className={iconClass(isActive)} /> {t('nav.admin.worksSync')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/messages" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <MessageSquare className={iconClass(isActive)} /> {t('nav.admin.messages')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/videos" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <Film className={iconClass(isActive)} /> {t('nav.admin.videos')}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/admin/notifications" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <Megaphone className={iconClass(isActive)} />{' '}
                      {t('nav.admin.notifications', { defaultValue: 'Notificări' })}
                    </>
                  )}
                </NavLink>
                <NavLink to="/app/changelog" className={linkClass}>
                  {({ isActive }) => (
                    <>
                      <Tag className={iconClass(isActive)} /> {t('nav.admin.changelog', { defaultValue: 'Changelog' })}
                    </>
                  )}
                </NavLink>
              </>
            ) : null}
          </>
        ) : null}
      </div>

      {/* Download App + Docs links — small footer-style nav */}
      <div className="space-y-0.5 border-t border-slate-100 px-3 py-2 dark:border-slate-800">
        <NavLink
          to="/app/download"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
              isActive
                ? 'bg-forest-50/80 text-forest-700 dark:bg-forest-900/30 dark:text-forest-200'
                : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300',
            )
          }
        >
          {({ isActive }) => (
            <>
              <Download
                className={cn(
                  'h-3.5 w-3.5',
                  isActive
                    ? 'text-forest-600 dark:text-forest-300'
                    : 'text-slate-300 dark:text-slate-600',
                )}
              />
              <span>{t('nav.download')}</span>
              {downloadHasUpdate && !isActive ? (
                <span
                  className="ml-auto inline-flex items-center gap-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white"
                  title={t('nav.downloadNewTitle', {
                    defaultValue: 'Linkuri actualizate recent',
                  })}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-white"
                    aria-hidden
                  />
                  {t('nav.new', { defaultValue: 'Nou' })}
                </span>
              ) : null}
            </>
          )}
        </NavLink>
        <NavLink
          to="/app/docs"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
              isActive
                ? 'bg-forest-50/80 text-forest-700 dark:bg-forest-900/30 dark:text-forest-200'
                : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300',
            )
          }
        >
          {({ isActive }) => (
            <>
              <BookOpen
                className={cn(
                  'h-3.5 w-3.5',
                  isActive
                    ? 'text-forest-600 dark:text-forest-300'
                    : 'text-slate-300 dark:text-slate-600',
                )}
              />
              {t('nav.docs')}
            </>
          )}
        </NavLink>
        <NavLink
          to="/contact"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
              isActive
                ? 'bg-forest-50/80 text-forest-700 dark:bg-forest-900/30 dark:text-forest-200'
                : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300',
            )
          }
        >
          {({ isActive }) => (
            <>
              <MessageSquare
                className={cn(
                  'h-3.5 w-3.5',
                  isActive
                    ? 'text-forest-600 dark:text-forest-300'
                    : 'text-slate-300 dark:text-slate-600',
                )}
              />
              {t('nav.contact', { defaultValue: 'Contact' })}
            </>
          )}
        </NavLink>
      </div>

      {/* User info footer */}
      {user ? (
        <div className="border-t border-slate-200 px-3 py-3 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-forest-100 text-xs font-bold text-forest-700 dark:bg-forest-900/40 dark:text-forest-300">
              {(user.fullName || user.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <button
                type="button"
                onClick={() => navigate('/app/profile')}
                className="truncate text-left text-sm font-medium text-slate-800 transition-colors hover:text-forest-700 dark:text-slate-200 dark:hover:text-forest-300"
                title={t('auth:profile.title')}
              >
                {user.fullName || user.username}
              </button>
              <div className="flex items-center gap-1.5">
                <span className="inline-flex w-fit rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {user.role?.replace('_', ' ') ?? 'User'}
                </span>
                <span className="text-[10px] text-slate-300 dark:text-slate-600">v{APP_VERSION}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void onLogout()}
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-slate-500 dark:hover:bg-red-900/30 dark:hover:text-red-400"
              title={t('action.logout')}
              aria-label={t('action.logout')}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </nav>
    </>
  );
}
